<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5>Detail Catatan Kas Harian</h5>
            </template>

            <div class="form-content-wrapper">
                <div class="mb-2">
                    <strong>Dibuat Pada:</strong>
                    {{ $helper.changeDateFormat(detail.createdAt || detail.tanggal) }}
                    {{ $helper.changeDateFormatGetHour(detail.createdAt || detail.tanggal) }}
                </div>
                <div class="mb-2"><strong>Shift:</strong> {{ getShiftLabel(detail.shift) }}</div>
                <div class="mb-2"><strong>Dibuat Oleh:</strong> {{ detail.createdBy || "-" }}</div>
                <div class="mb-2"><strong>Staff Opname:</strong> {{ detail.saksi || "-" }}</div>
                <div class="mb-2">
                    <strong>Status:</strong>
                    <span class="ml-1" :class="getStatusClass(detail.status)">
                        {{ getStatusLabel(detail.status) }}
                    </span>
                </div>
                <div class="mb-2">
                    <strong>Diverifikasi Staff Opname Pada:</strong> {{ detail.verifiedAt || "-" }}
                </div>
                <div v-if="isRejectedStatus(detail.status)" class="mb-2">
                    <strong>Dibatalkan Oleh:</strong> {{ detail.cancelledBy || "-" }}
                </div>
                <div v-if="isRejectedStatus(detail.status)" class="mb-2">
                    <strong>Dibatalkan Pada:</strong> {{ detail.cancelledAt || "-" }}
                </div>
                <div v-if="isRejectedStatus(detail.status)" class="mb-2">
                    <strong>Alasan Dibatalkan:</strong> {{ detail.rejectionReason || "-" }}
                </div>
                <div class="mb-4">
                    <strong>Catatan:</strong> {{ detail.catatan || "-" }}
                </div>

                <h6 class="form-title">Rincian Fisik Uang Kas Laci</h6>
                <b-table-simple small bordered responsive class="mb-3">
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Qty</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in getDetailRows('laci')" :key="`laci-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <h6 class="form-title">Rincian Fisik Uang Kas Brankas</h6>
                <b-table-simple small bordered responsive class="mb-3">
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Qty</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in getDetailRows('brankas')" :key="`brankas-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <h6 class="form-title">Rincian Fisik Uang Kas Kecil</h6>
                <b-table-simple small bordered responsive class="mb-3">
                    <b-thead>
                        <b-tr>
                            <b-th>Pecahan</b-th>
                            <b-th class="text-right">Qty</b-th>
                            <b-th class="text-right">Total</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="item in getDetailRows('kasKecil')" :key="`kas-kecil-${item.pecahanId}`">
                            <b-td>Rp. {{ item.pecahanLabel }}</b-td>
                            <b-td class="text-right">{{ item.qty }}</b-td>
                            <b-td class="text-right">Rp. {{ $helper.addThousandSeparator(item.totalNominal) }}</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <div class="mt-3">
                    <h6 class="form-title">Saldo Sistem</h6>
                    <div class="mb-2">
                        <strong>Kas Laci:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.saldoKasLaci) }}
                    </div>
                    <div class="mb-2">
                        <strong>Kas Brankas:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.saldoKasBrankas) }}
                    </div>
                    <div class="mb-2">
                        <strong>Total Kas:</strong>
                        Rp. {{ $helper.addThousandSeparator(getSaldoSistemTotal()) }}
                    </div>

                    <h6 class="form-title mt-4">Saldo Fisik</h6>
                    <div class="mb-2">
                        <strong>Kas Laci:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.totalInputLaci) }}
                    </div>
                    <div class="mb-2">
                        <strong>Kas Brankas:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.totalInputBrankas) }}
                    </div>
                    <div class="mb-2">
                        <strong>Total Kas:</strong>
                        Rp. {{ $helper.addThousandSeparator(getSaldoFisikTotal()) }}
                    </div>

                    <h6 class="form-title mt-4">Inventaris</h6>
                    <div class="mb-1">
                        <strong>Materai Tersedia:</strong>
                        {{ getMateraiQtyFromNominal(detail.totalKasMaterai) }} lembar
                    </div>
                    <div class="mb-4">
                        <strong>Materai Terjual:</strong>
                        {{ getMateraiQtyFromNominal(detail.totalKasMateraiTerjual) }} lembar
                        (Nilai Rp. {{ $helper.addThousandSeparator(detail.totalKasMateraiTerjual) }})
                    </div>
                    <div class="mb-2">
                        <strong>Selisih Laci:</strong>
                        <span :class="getSelisihClassByValue(getSelisihLaciValue())">
                            Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihLaciValue())) }}
                        </span>
                    </div>
                    <div class="mb-0">
                        <strong>Selisih Brankas:</strong>
                        <span :class="getSelisihClassByValue(getSelisihBrankasValue())">
                            Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihBrankasValue())) }}
                        </span>
                    </div>
                    <div class="mb-0 mt-2">
                        <strong>Total Selisih:</strong>
                        <span :class="getSelisihClassByValue(getTotalSelisihValue())">
                            Rp. {{ $helper.addThousandSeparator(Math.abs(getTotalSelisihValue())) }}
                        </span>
                    </div>

                    <h6 class="form-title mt-4">Rekonsiliasi Kas Kecil</h6>
                    <div class="mb-2">
                        <strong>Saldo Sistem:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.saldoKasKecil) }}
                    </div>
                    <div class="mb-2">
                        <strong>Saldo Fisik:</strong>
                        Rp. {{ $helper.addThousandSeparator(detail.totalInputKasKecil) }}
                    </div>
                    <div class="mb-0">
                        <strong>Selisih:</strong>
                        <span :class="getSelisihClassByValue(getSelisihKasKecilValue())">
                            Rp. {{ $helper.addThousandSeparator(Math.abs(getSelisihKasKecilValue())) }}
                        </span>
                    </div>
                </div>

            </div>

            <div class="form-wrapper-btn submit-btn-wrapper">
                <b-button
                    type="button"
                    variant="secondary"
                    @click="goBack"
                >
                    Kembali
                </b-button>
            </div>
        </b-card>
    </div>
</template>

<script>
import { ENUM_PECAHAN_IDS, ENUM_SHIFT, ENUM_TYPE } from "@/constants/operasionalKas";

const DENOMINATIONS = Object.values(ENUM_PECAHAN_IDS)
    .sort((first, second) => first.id - second.id);

function buildPecahanData(denominations) {
    const pecahan = {};
    denominations.forEach((item) => {
        pecahan[item.id] = "0";
    });
    return pecahan;
}

export default {
    name: "detail-catatan-kas",
    data() {
        return {
            initStatus: 0,
            denominations: DENOMINATIONS,
            detail: {
                tanggal: null,
                createdAt: null,
                shift: null,
                createdBy: "-",
                saksi: "-",
                saldoProgram: 0,
                saldoKasLaci: 0,
                saldoKasBrankas: 0,
                saldoKasKecil: 0,
                totalInputLaci: 0,
                totalInputBrankas: 0,
                totalInputKasKecil: 0,
                totalKasMaterai: 0,
                totalKasMateraiTerjual: 0,
                kelipatanMaterai: 0,
                totalDetails: 0,
                selisih: 0,
                selisihLaci: 0,
                selisihBrankas: 0,
                selisihKasKecil: 0,
                catatan: "",
                rejectionReason: "",
                cancelledBy: "",
                cancelledAt: "-",
                status: 1,
                verifiedAt: "-",
                pecahanLaci: buildPecahanData(DENOMINATIONS),
                pecahanBrankas: buildPecahanData(DENOMINATIONS),
                pecahanKasKecil: buildPecahanData(DENOMINATIONS),
            },
        };
    },
    methods: {
        mapDetailsToPecahan(details = []) {
            const pecahan = buildPecahanData(this.denominations);
            details.forEach((item) => {
                const pecahanId = Number(item.pecahanId);
                if (Object.prototype.hasOwnProperty.call(pecahan, pecahanId)) {
                    pecahan[pecahanId] = `${Number(item.qty || 0)}`;
                }
            });
            return pecahan;
        },
        init() {
            this.initStatus = 0;
            const detailId = this.$route.params.id;

            this.$axios.get(`log-book-kasir/${detailId}`)
                .then((response) => {
                    const data = response.data || {};
                    const rawDetails = Array.isArray(data.details) ? data.details : [];
                    const detailsLaci = Array.isArray(data.detailsLaci)
                        ? data.detailsLaci
                        : rawDetails.filter((item) => Number(item.type) === ENUM_TYPE.LACI.id);
                    const detailsBrankas = Array.isArray(data.detailsBrankas)
                        ? data.detailsBrankas
                        : rawDetails.filter((item) => Number(item.type) === ENUM_TYPE.BRANKAS.id);
                    const detailsKasKecil = Array.isArray(data.detailsKasKecil)
                        ? data.detailsKasKecil
                        : rawDetails.filter((item) => Number(item.type) === ENUM_TYPE.KAS_KECIL.id);

                    this.detail = {
                        tanggal: data.tanggal,
                        createdAt: data.createdAt || data.tanggal,
                        shift: Number(data.shift),
                        createdBy: data.createdBy || "-",
                        saksi: data.saksi || "-",
                        saldoProgram: Number(data.saldoProgram || 0),
                        saldoKasLaci: Number(data.saldoLaci || data.saldoKasLaci || 0),
                        saldoKasBrankas: Number(data.saldoBrankas || data.saldoKasBrankas || 0),
                        saldoKasKecil: Number(data.saldoKasKecil || 0),
                        totalInputLaci: Number(data.totalKasLaci || data.totalInputLaci || 0),
                        totalInputBrankas: Number(data.totalKasBrankas || data.totalInputBrankas || 0),
                        totalInputKasKecil: Number(data.totalKasKecil || data.totalInputKasKecil || 0),
                        totalKasMaterai: Number(data.totalKasMaterai || 0),
                        totalKasMateraiTerjual: Number(data.totalKasMateraiTerjual || 0),
                        kelipatanMaterai: Number(data.kelipatanMaterai || 0),
                        totalDetails: Number(data.totalDetails || 0),
                        selisih: data.selisih === null || data.selisih === undefined ? null : Number(data.selisih || 0),
                        selisihLaci: Number(data.selisihLaci || 0),
                        selisihBrankas: Number(data.selisihBrankas || 0),
                        selisihKasKecil: Number(data.selisihKasKecil || 0),
                        catatan: data.note || "",
                        rejectionReason: data.rejectedNote || "",
                        cancelledBy: data.rejectedBy || "",
                        cancelledAt: this.formatDateTime(data.rejectedAt),
                        status: data.status || 1,
                        verifiedAt: this.formatDateTime(data.staffProcessedAt),
                        pecahanLaci: this.mapDetailsToPecahan(detailsLaci),
                        pecahanBrankas: this.mapDetailsToPecahan(detailsBrankas),
                        pecahanKasKecil: this.mapDetailsToPecahan(detailsKasKecil),
                    };
                })
                .then(() => {
                    this.initStatus = 1;
                })
                .catch((error) => {
                    console.log(error);
                    this.$helper.parseError(this, error);
                    this.initStatus = -1;
                });
        },
        getShiftLabel(shiftId) {
            if (Number(shiftId) === ENUM_SHIFT.PAGI.id) return "Pagi";
            if (Number(shiftId) === ENUM_SHIFT.SIANG.id) return "Siang";
            if (Number(shiftId) === ENUM_SHIFT.MALAM.id) return "Malam";
            return "-";
        },
        getDetailRows(scope) {
            let source = this.detail.pecahanLaci;
            if (scope === "brankas") source = this.detail.pecahanBrankas;
            if (scope === "kasKecil") source = this.detail.pecahanKasKecil;
            return this.denominations.map((pecahan) => {
                const qty = Number(source[pecahan.id] || 0);
                return {
                    pecahanId: pecahan.id,
                    pecahanLabel: pecahan.label,
                    qty,
                    totalNominal: qty * Number(pecahan.value || 0),
                };
            });
        },
        getSelisihClassByValue(selisih) {
            if (Number(selisih) < 0) return "text-success";
            if (Number(selisih) > 0) return "text-danger";
            return "";
        },
        getStatusLabel(status) {
            if (Number(status) === 2) return "Dibatalkan";
            if (Number(status) === 3) return "Diverifikasi";
            return "Pending";
        },
        getStatusClass(status) {
            if (Number(status) === 2) return "status-box-md status-box-danger";
            if (Number(status) === 3) return "status-box-md status-box-success";
            return "status-box-md status-box-warning";
        },
        isRejectedStatus(status) {
            return Number(status) === 2;
        },
        formatDateTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-";
            return `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`;
        },
        getSelisihLaciValue() {
            const selisih = Number(this.detail.selisihLaci || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getSelisihBrankasValue() {
            const selisih = Number(this.detail.selisihBrankas || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getSelisihKasKecilValue() {
            const selisih = Number(this.detail.selisihKasKecil || 0);
            if (Number.isNaN(selisih)) return 0;
            return selisih;
        },
        getTotalSelisihValue() {
            return this.getSelisihLaciValue()
                + this.getSelisihBrankasValue();
        },
        getSaldoSistemTotal() {
            const saldoLaci = Number(this.detail.saldoKasLaci || 0);
            const saldoBrankas = Number(this.detail.saldoKasBrankas || 0);
            return saldoLaci + saldoBrankas;
        },
        getSaldoFisikTotal() {
            const totalLaci = Number(this.detail.totalInputLaci || 0);
            const totalBrankas = Number(this.detail.totalInputBrankas || 0);
            return totalLaci + totalBrankas;
        },
        getMateraiQtyFromNominal(value) {
            if (this.detail.kelipatanMaterai <= 0) return 0;
            return Number(value || 0) / this.detail.kelipatanMaterai;
        },
        goBack() {
            const path = this.$route.path.split("/");
            path.pop();
            this.$router.push({ path: path.join("/") });
        },
    },
    created() {
        this.init();
    },
};
</script>
